import { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { getWalletData } from '@/services/walletService';
import { NotificationBell } from '@/components/client/notification/NotificationBell';
import { getAdminBasePath } from '@/utils/adminPaths';

interface TopbarProps {
  page: string;
}

const Topbar = ({ page }: TopbarProps) => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // State for user data that updates reactively
  const [communityName, setCommunityName] = useState<string | null>(null);
  const [communityAvatar, setCommunityAvatar] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clientAvatar, setClientAvatar] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  // Fetch wallet balance when page is community or admin
  useEffect(() => {
    if (page === 'community' || page === 'admin') {
      const fetchWallet = async () => {
        try {
          const data = await getWalletData();
          if (data && typeof data.balance === 'number') {
            setWalletBalance(data.balance);
          } else {
            setWalletBalance(12450000);
          }
        } catch {
          setWalletBalance(12450000);
        }
      };
      fetchWallet();
    }
  }, [page, location]);

  // Function to refresh user data from localStorage
  const refreshUserData = () => {
    if (page === 'community') {
      setCommunityName(localStorage.getItem('communityName'));
      setCommunityAvatar(localStorage.getItem('communityAvatar'));
    } else if (page === 'client') {
      setClientName(localStorage.getItem('clientName'));
      setClientAvatar(localStorage.getItem('clientAvatar'));
    }
  };

  // Update user data from localStorage when location changes
  useEffect(() => {
    refreshUserData();
  }, [location, page]);

  // Listen for custom localStorage update events from other components
  useEffect(() => {
    const handleStorageUpdate = () => {
      refreshUserData();
    };

    window.addEventListener('localStorageUserUpdate', handleStorageUpdate);
    return () => {
      window.removeEventListener('localStorageUserUpdate', handleStorageUpdate);
    };
  }, [page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/80 bg-white/95 backdrop-blur-sm shrink-0 sticky top-0 z-10">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggleSidebar}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center cursor-pointer"
          title={isCollapsed ? 'Perluas Sidebar' : 'Ciutkan Sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            icon={isCollapsed ? 'mdi:chevron-double-right' : 'mdi:chevron-double-left'}
            className="w-5 h-5 transition-transform duration-200"
          />
        </button>
      </div>

      <div className="flex items-center gap-4">
        {page === 'client' ? <NotificationBell /> : null}
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center justify-between">
            {page === 'admin' && (
              <div className="flex items-center gap-2 mr-2">
                <button
                  type="button"
                  onClick={() => navigate(`${getAdminBasePath()}/wallet`)}
                  className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/20 transition-all cursor-pointer shadow-2xs"
                  title="Lihat Saldo & Wallet"
                >
                  <Icon icon="ph:wallet-bold" className="w-4 h-4 text-[#FC480D]" />
                  <span className="text-xs text-gray-500 font-medium">Saldo</span>
                  <span className="text-xs font-semibold text-[#0E1726]">
                    Rp. {(walletBalance ?? 12450000).toLocaleString('id-ID')}
                  </span>
                </button>
                <button
                  type="button"
                  className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-500 transition-all cursor-pointer shadow-2xs"
                  title="Notifikasi"
                >
                  <Icon icon="ph:bell-bold" className="w-4 h-4" />
                </button>
              </div>
            )}

            {page === 'community' && (
              <button
                onClick={() => navigate('/host/wallet')}
                className="flex items-center mr-2 border-r border-gray-300 gap-2 hover:bg-gray-50 ps-2 pe-5 py-1 rounded transition-colors"
              >
                <Icon icon="mdi:wallet" className="h-6 w-6 text-primary-500" />
                <div className="flex flex-col text-left">
                  <span className="text-[10px] text-gray-500 font-normal leading-tight">
                    Saldo Tersedia
                  </span>
                  <span className="text-sm font-semibold text-gray-800 leading-tight">
                    Rp {walletBalance !== null ? walletBalance.toLocaleString('id-ID') : '0'}
                  </span>
                </div>
              </button>
            )}

            {page !== 'admin' && (
              <>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded transition-colors"
                  aria-expanded={isDropdownOpen}
                >
                  {page === 'community' ? (
                    <>
                      <span className="text-sm font-normal text-gray-700">{communityName}</span>
                      {communityAvatar ? (
                        <img
                          src={communityAvatar}
                          alt="Avatar"
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <Icon icon="mdi:account-circle-outline" className="h-6 w-6 text-primary-500" />
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-normal text-gray-700">{clientName}</span>
                      {clientAvatar ? (
                        <img
                          src={clientAvatar}
                          alt="Avatar"
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <Icon icon="mdi:account-circle-outline" className="h-6 w-6 text-primary-500" />
                      )}
                    </>
                  )}
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        if (page === 'client') {
                          navigate('/profile');
                          return;
                        }
                        if (page === 'community') {
                          navigate('/host/profile-settings');
                          return;
                        }
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Icon icon="mdi:account-circle-outline" className="h-4 w-4" />
                      Profile Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 transition-colors text-red-500"
                    >
                      <Icon icon="mdi:logout" className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Topbar;
